import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/utils';
import { 
  ShoppingCart, DollarSign, Package, TrendingUp,
  ExternalLink, BarChart3, Clock, CheckCircle, AlertCircle,
  ArrowRight, CalendarDays, Users, Ban, AlertTriangle
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [establishment, setEstablishment] = useState(null);
  const [stats, setStats] = useState(null);
  const [weeklyData, setWeeklyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEstablishment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/establishments`);
      if (response.data.length > 0) {
        setEstablishment(response.data[0]);
        setError(null);
      } else {
        setError('no_establishment');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('fetch_error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    if (!establishment) return;
    try {
      const response = await axios.get(`${API}/establishments/${establishment.id}/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error:', err);
    }
  }, [establishment]);

  const fetchWeeklyData = useCallback(async () => {
    if (!establishment) return;
    try {
      const response = await axios.get(`${API}/establishments/${establishment.id}/reports/daily?days=7`);
      setWeeklyData(response.data.daily_data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  }, [establishment]);

  useEffect(() => {
    fetchEstablishment();
  }, [fetchEstablishment]);

  useEffect(() => {
    if (establishment) {
      fetchStats();
      fetchWeeklyData();
      const interval = setInterval(fetchStats, 30000);
      return () => clearInterval(interval);
    }
  }, [establishment, fetchStats, fetchWeeklyData]);

  if (loading) {
    return (
      <AdminLayout establishmentName={establishment?.name}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error === 'no_establishment') {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mb-4" />
          <h2 className="text-2xl font-bold text-slate-700 mb-2">Nenhum estabelecimento vinculado</h2>
          <p className="text-slate-500">Entre em contato com o administrador do sistema.</p>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: 'Pedidos Pendentes',
      value: stats?.pending_orders || 0,
      icon: Clock,
      color: 'text-amber-500',
      bgColor: 'bg-amber-50',
      href: '/admin/pedidos',
      highlight: stats?.pending_orders > 0
    },
    {
      title: 'Pedidos Hoje',
      value: stats?.today_orders || 0,
      icon: ShoppingCart,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      href: '/admin/pedidos'
    },
    {
      title: 'Faturamento Hoje',
      value: formatCurrency(stats?.today_revenue || 0),
      icon: DollarSign,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      href: '/admin/reports'
    },
    {
      title: 'Produtos Cadastrados',
      value: stats?.total_products || 0,
      icon: Package,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      href: '/admin/products'
    }
  ];

  return (
    <AdminLayout establishmentName={establishment?.name}>
      <div className="space-y-6">
        {/* Blocked Establishment Banner */}
        {establishment?.is_blocked && (
          <div className="bg-red-500 text-white p-4 rounded-lg" data-testid="blocked-banner">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-white/20 rounded-lg">
                <Ban className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Sua Loja Está Bloqueada
                </h3>
                <p className="text-red-100 mt-1">
                  {establishment.blocked_reason || 'Sua loja foi bloqueada. Por favor, regularize sua situação para voltar a operar.'}
                </p>
                <p className="text-red-200 text-sm mt-2">
                  Entre em contato com o suporte para mais informações sobre como regularizar.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              Dashboard
            </h1>
            <p className="text-slate-500">Acompanhe o desempenho do seu negócio</p>
          </div>
          <Button
            variant="outline"
            onClick={() => window.open(`/loja/${establishment?.slug}`, '_blank')}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Ver Loja
          </Button>
        </div>

        {/* Stats Cards - Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card 
                key={index} 
                className={`cursor-pointer hover:shadow-md transition-shadow ${card.highlight ? 'ring-2 ring-amber-500 animate-pulse' : ''}`}
                onClick={() => navigate(card.href)}
                data-testid={`stat-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">{card.title}</p>
                      <p className="text-2xl font-bold text-slate-800 mt-1">{card.value}</p>
                    </div>
                    <div className={`p-3 rounded-full ${card.bgColor}`}>
                      <Icon className={`w-6 h-6 ${card.color}`} />
                    </div>
                  </div>
                  <div className="flex items-center mt-4 text-sm text-slate-500">
                    <span>Ver detalhes</span>
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow bg-gradient-to-br from-red-500 to-red-600 text-white"
            onClick={() => navigate('/admin/pedidos')}
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Gerenciar Pedidos</h3>
                <p className="text-red-100 text-sm">Kanban em tempo real</p>
              </div>
              <ShoppingCart className="w-10 h-10 opacity-50" />
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow bg-gradient-to-br from-blue-500 to-blue-600 text-white"
            onClick={() => navigate('/admin/products')}
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Produtos</h3>
                <p className="text-blue-100 text-sm">Gerenciar cardápio</p>
              </div>
              <Package className="w-10 h-10 opacity-50" />
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow bg-gradient-to-br from-green-500 to-green-600 text-white"
            onClick={() => navigate('/admin/reports')}
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Relatórios</h3>
                <p className="text-green-100 text-sm">Análises detalhadas</p>
              </div>
              <TrendingUp className="w-10 h-10 opacity-50" />
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                Faturamento - Últimos 7 dias
              </CardTitle>
            </CardHeader>
            <CardContent>
              {weeklyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('pt-BR', { weekday: 'short' });
                      }}
                      stroke="#94a3b8"
                    />
                    <YAxis 
                      tickFormatter={(value) => `R$${value}`}
                      stroke="#94a3b8"
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Faturamento']}
                      labelFormatter={(label) => new Date(label).toLocaleDateString('pt-BR')}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#22c55e" 
                      strokeWidth={2}
                      dot={{ fill: '#22c55e' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-400">
                  Sem dados disponíveis
                </div>
              )}
            </CardContent>
          </Card>

          {/* Orders Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-blue-500" />
                Pedidos - Últimos 7 dias
              </CardTitle>
            </CardHeader>
            <CardContent>
              {weeklyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('pt-BR', { weekday: 'short' });
                      }}
                      stroke="#94a3b8"
                    />
                    <YAxis stroke="#94a3b8" />
                    <Tooltip 
                      formatter={(value) => [value, 'Pedidos']}
                      labelFormatter={(label) => new Date(label).toLocaleDateString('pt-BR')}
                    />
                    <Bar dataKey="orders" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-400">
                  Sem dados disponíveis
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Summary Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-500" />
              Resumo Geral
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-slate-800">{stats?.total_orders || 0}</p>
                <p className="text-sm text-slate-500">Total de Pedidos</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-slate-800">{stats?.total_products || 0}</p>
                <p className="text-sm text-slate-500">Produtos</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-slate-800">{stats?.total_categories || 0}</p>
                <p className="text-sm text-slate-500">Categorias</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{formatCurrency(stats?.today_revenue || 0)}</p>
                <p className="text-sm text-slate-500">Faturamento Hoje</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
